import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderComponent } from '../../../modules/components/loader/loader.component';
import { StandardReportService } from '../service/standard-report.service';
import { UserSwitcherService } from '../service/user-switcher.service';
import { UserService } from '../service/user.service';
import { WeeklyReportSessionService } from '../service/weekly-report-session.service';
import { TranslateModule } from '@ngx-translate/core';

/**
* Handles redirection and authentication flow for weekly report access by generating a temporary token for a specified user, checking access/redirection rules, optionally initiating OTP flows, and routing the user to the appropriate weekly report pages.
* @component
* @selector app-weekly-report-auth-redirect
* @example
*   <app-weekly-report-auth-redirect></app-weekly-report-auth-redirect>
* @input none - This component does not expose any @Input() properties; it reads parameters from the route query string (redirectUrl, customer_id, user_id, date).
* @output none - This component does not emit @Output() events; it performs navigation and side effects (localStorage and user verifier) internally.
*
* Description:
* - Purpose: Orchestrates authentication and redirection for weekly report links that include query parameters for user and customer context. It requests a usable token for the target user, persists token and identifiers in localStorage, sets a decoded verifier in the UserService, checks whether to redirect to an older flow or require OTP, and navigates to the appropriate route (weekly report or OTP verification).
* - Key behaviors:
*   - Reads query parameters (redirectUrl, customer_id, user_id, date) on initialization.
*   - Requests a temporary token for the provided user/customer via StandardReportService.generateTokenByUserIdForWeeklyReport.
*   - Persists token, userId, and customerId to localStorage and sets the verifier in UserService after decoding.
*   - Validates redirection/OTP requirement via StandardReportService.checkWeeklyStandardReportRedirection and routes accordingly:
*     - If redirect_to_old is true, exposes redirectToOldUrl for an external/older flow.
*     - If skip_otpconfigured (isOtpVerificationRequired) is true, navigates to /weeklyreport; otherwise navigates to /weeklyreportotpverification.
*   - Can trigger OTP generation for authentication via UserSwitcherService.generateOTPUserSwitcher.
* - Side effects:
*   - Writes token, userId, customerId to localStorage.
*   - Decodes and sets a verifier on UserService.
*   - Performs Router navigation to internal routes.
*   - Logs responses and errors to console for debugging and updates local loading/error flags.
*
* Key properties:
* - redirectUrl: string | null — target redirect URL from query params (if provided).
* - customerId: string | null — customer identifier parsed from query params or API result.
* - userId: string | null — user identifier parsed from query params or API result.
* - date: string | null — encrypted date parameter from query params.
* - token: string | null — temporary token returned by token generation API.
* - isOtpVerificationRequired: boolean — whether OTP is skipped/configured for the user.
* - isLoading: boolean — indicates overall loading state while fetching token or checking access.
* - isFetchingTokenFailed: boolean — indicates failure to generate token.
* - isHandelingAccess: boolean — indicates failure to handle access/redirection check.
* - redirectToOld: boolean — indicates whether flow requires redirecting to an older flow.
* - redirectToOldUrl: string — URL returned by the API for the old redirection flow.
*
* Key methods:
* - ngOnInit(): lifecycle hook that reads route query parameters and initiates token generation.
* - generateTokenByUserIdForWeeklyReport(): calls StandardReportService to generate a token for the specified user/customer, stores results, sets verifier, and then calls handleWeeklyReportAccess(). Updates loading and error flags accordingly.
* - handleWeeklyReportAccess(): calls StandardReportService to determine redirection rules and OTP requirements. Sets redirectToOld and redirectToOldUrl when necessary and navigates to the appropriate internal route or marks access handling failure.
* - generateOtpToAuthenticateUserForWeeklyReport(): requests OTP generation via UserSwitcherService for the current user/token; logs results and errors.
*
* Lifecycle hooks:
* - ngOnInit: Subscribes to ActivatedRoute.queryParamMap to extract required parameters and triggers token generation. All network interactions are initiated from this hook.
*
* Error handling and observability:
* - Network responses are checked for HTTP 200 status and a success flag in the body. Failures set boolean flags (isFetchingTokenFailed, isHandelingAccess) and clear loading states. Errors are logged to the console.
*/
@Component({
  selector: 'app-weekly-report-auth-redirect',
  standalone: true,
  imports: [LoaderComponent, CommonModule, TranslateModule],
  templateUrl: './weekly-report-auth-redirect.component.html',
  styleUrl: './weekly-report-auth-redirect.component.css'
})
export class WeeklyReportAuthRedirectComponent implements OnInit {

  redirectUrl: string | null = null;
  customerId: string | null = null;
  userId: string | null = null;
  date: string | null = null;
  token: string | null = null;
  isOtpVerificationRequired: boolean = false;
  isLoading: boolean = true;
  isFetchingTokenFailed: boolean = false;
  isHandelingAccess: boolean = false;
  redirectToOld: boolean = false;
  redirectToOldUrl: string = '';

  constructor(
    private route: ActivatedRoute,
    private standardreportservice: StandardReportService,
    private user: UserService,
    private userSwitcherService: UserSwitcherService,
    private router: Router,
    private weeklySession: WeeklyReportSessionService,
  ) { }

  /**
  * Read query parameters (redirectUrl, customer_id, user_id, date), log them to the console, and trigger token generation for the weekly report.
  * @example
  * gOnInit()
  * // void
  * @returns {void} Initializes component state from route query parameters and starts token generation for the weekly report.
  **/
  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.redirectUrl = params.get('redirectUrl');
      this.customerId = params.get('customer_id');
      this.userId = params.get('user_id');
      this.date = params.get('date');
    });
    this.generateTokenByUserIdForWeeklyReport();
  }

  /**
  * Generate a token for the weekly report using the component's current userId and customerId, store it through WeeklyReportSessionService (report-scoped, never the app session key), update component state, decode and set the verifier, and proceed to weekly report access.
  * @example
  * enenerateTokenByUserIdForWeeklyReport()
  * undefined
  * @param {void} none - This method does not accept any arguments; it uses instance properties (this.userId, this.customerId).
  * @returns {void} No return value; the method performs side effects (state updates, report-token storage, and calls to handleWeeklyReportAccess).
  **/
  generateTokenByUserIdForWeeklyReport() {
    sessionStorage.setItem('encryptedCustomerId', String(this.customerId));
    sessionStorage.setItem('encryptedUserId', String(this.userId));
    sessionStorage.setItem('encryptedDate', String(this.date));
    const params = {
      user_id: this.userId,
      customer_id: this.customerId,
    }

    this.standardreportservice.generateTokenByUserIdForWeeklyReport(params).subscribe({
      next: (response: HttpResponse<any>) => {
        if (response.status === 200 && response.body?.success) {
          const { token, user_id: userId, customer_id: customerId, verifier } = response.body.result;
          this.token = token;
          this.userId = userId;
          this.customerId = customerId;

          // The ids are read back from the token itself (sub / rt.cid); they are no
          // longer mirrored into editable sessionStorage keys.
          this.weeklySession.setToken(String(this.token));

          const decodedVerifier = atob(verifier);
          this.user.setVerifier(decodedVerifier);
          this.handleWeeklyReportAccess();
        } else {
          console.error('Failed to generate token. Invalid response status or empty body');
          this.isFetchingTokenFailed = true;
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.isFetchingTokenFailed = true;
        this.isLoading = false;
        console.error('Error generating token:', error);
      }
    }
    );
  }

  /**
  * Check weekly report access and redirect the user based on the server response.
  * @example
  * andleWeeklyReportAccess()
  * undefined
  * @param {{void}} {{none}} - This function does not accept any parameters.
  * @returns {{void}} Performs navigation and state updates (no return value).
  **/
  handleWeeklyReportAccess() {
    const params = {
      customer_id: Number(this.customerId),
      user_id: Number(this.userId), 
      encrypted_date: this.date,
    } 
    this.standardreportservice.checkWeeklyStandardReportRedirection(params, String(this.token)).subscribe({
      next: (response: HttpResponse<any>) => {
        if (response.status === 200 && response.body?.success) {
          const { redirect_to_old: redirectToOld, redirection_url: redirectToOldUrl, skip_otpconfigured: isOtpVerificationRequired } = response.body.result;
          this.redirectToOld = redirectToOld;
          if (this.redirectToOld && redirectToOldUrl) {
            this.redirectToOldUrl = redirectToOldUrl;
            window.location.href = this.redirectToOldUrl;
            return;
          }
          this.isOtpVerificationRequired = isOtpVerificationRequired;
          if (this.isOtpVerificationRequired) {
            this.router.navigate(['/weeklyreport']);
          } else {
            this.router.navigate(['/weeklyreportotpverification']);
          }
        } else {
          this.isHandelingAccess = true;
          this.isLoading = false;
          console.error('Failed to check redirection. Invalid response status or empty body');
        }
      },
      error: (error) => {
        this.isHandelingAccess = true;
        this.isLoading = false;
        console.error('Error checking redirection:', error);
      }
    });
  }

  /**
  * Generate an OTP via UserSwitcherService to authenticate the current user for accessing the weekly report.
  * @example
  * enegateOtpToAuthenticateUserForWeeklyReport()
  * undefined
  * @returns {{void}} No value is returned; the function triggers an HTTP request and logs the result or error.
  */
  generateOtpToAuthenticateUserForWeeklyReport() {
    const params = {
      user_id: this.userId,
      token: this.token
    };
    this.userSwitcherService.generateOTPUserSwitcher(params).subscribe({
      next: (response: HttpResponse<any>) => {
        if (response.status === 200 && response.body?.success) {
          // OTP generated successfully
        } else {
          console.error('Failed to generate OTP. Invalid response status or empty body');
        }
      },
      error: (error) => {
        console.error('Error generating OTP:', error);
      }
    });
  }

}
